import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";

const FrenchTest = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [testStarted, setTestStarted] = useState(false);

  const questions = [
    // A1 Level Questions (Easy)
    {
      level: "A1",
      passage: "Bonjour ! Je m'appelle Marie. J'ai 25 ans. Je suis française. J'habite à Paris. J'aime le chocolat et les chats.",
      question: "Où habite Marie ?",
      options: [
        { id: "a", text: "À Lyon", correct: false },
        { id: "b", text: "À Paris", correct: true },
        { id: "c", text: "À Marseille", correct: false },
        { id: "d", text: "À Nice", correct: false }
      ]
    },
    {
      level: "A1",
      passage: "Paul va au supermarché. Il achète du pain, du lait et des pommes. Il paie avec sa carte bancaire.",
      question: "Que fait Paul au supermarché ?",
      options: [
        { id: "a", text: "Il travaille", correct: false },
        { id: "b", text: "Il mange", correct: false },
        { id: "c", text: "Il achète des choses", correct: true },
        { id: "d", text: "Il étudie", correct: false }
      ]
    },
    {
      level: "A1",
      passage: "Aujourd'hui, il fait beau. Le soleil brille. Les enfants jouent dans le parc. Sophie porte une robe rouge.",
      question: "Quel temps fait-il ?",
      options: [
        { id: "a", text: "Il pleut", correct: false },
        { id: "b", text: "Il neige", correct: false },
        { id: "c", text: "Il fait beau", correct: true },
        { id: "d", text: "Il fait froid", correct: false }
      ]
    },
    // A2 Level Questions (Elementary)
    {
      level: "A2",
      passage: "La semaine dernière, Thomas est allé en vacances à la mer avec sa famille. Ils ont nagé tous les jours et ont mangé beaucoup de poissons frais. Le dernier jour, ils ont visité un petit village de pêcheurs très pittoresque.",
      question: "Qu'est-ce que Thomas a fait pendant ses vacances ?",
      options: [
        { id: "a", text: "Il a travaillé", correct: false },
        { id: "b", text: "Il est resté à la maison", correct: false },
        { id: "c", text: "Il est allé à la mer avec sa famille", correct: true },
        { id: "d", text: "Il a visité des musées", correct: false }
      ]
    },
    {
      level: "A2",
      passage: "Emma adore cuisiner. Chaque dimanche, elle prépare un grand repas pour ses amis. Elle commence toujours par faire les courses au marché le samedi matin. Elle choisit des produits frais et de saison.",
      question: "Quand Emma fait-elle ses courses ?",
      options: [
        { id: "a", text: "Le dimanche matin", correct: false },
        { id: "b", text: "Le samedi matin", correct: true },
        { id: "c", text: "Le vendredi soir", correct: false },
        { id: "d", text: "Tous les jours", correct: false }
      ]
    },
    {
      level: "A2",
      passage: "Marc a perdu son téléphone hier dans le bus. Il était très inquiet parce que tous ses contacts et ses photos étaient dedans. Heureusement, quelqu'un l'a trouvé et l'a rapporté au bureau des objets trouvés.",
      question: "Pourquoi Marc était-il inquiet ?",
      options: [
        { id: "a", text: "Il a raté son bus", correct: false },
        { id: "b", text: "Il a perdu son téléphone avec ses données", correct: true },
        { id: "c", text: "Il a oublié son sac", correct: false },
        { id: "d", text: "Il était en retard", correct: false }
      ]
    },
    // B1 Level Questions (Intermediate)
    {
      level: "B1",
      passage: "Les réseaux sociaux ont profondément transformé notre façon de communiquer. Si d'un côté ils permettent de rester en contact avec des amis éloignés, de l'autre ils peuvent créer une dépendance et isoler les gens de leur entourage réel. Il est important de trouver un équilibre.",
      question: "Selon le texte, quel est le principal défi des réseaux sociaux ?",
      options: [
        { id: "a", text: "Ils sont trop chers", correct: false },
        { id: "b", text: "Il faut trouver un équilibre entre avantages et inconvénients", correct: true },
        { id: "c", text: "Ils ne fonctionnent pas bien", correct: false },
        { id: "d", text: "Personne ne les utilise", correct: false }
      ]
    },
    {
      level: "B1",
      passage: "Le télétravail s'est généralisé ces dernières années. Bien qu'il offre plus de flexibilité et réduise le temps de transport, certains employés se plaignent du manque d'interactions sociales et de la difficulté à séparer vie professionnelle et vie personnelle.",
      question: "Quel inconvénient du télétravail est mentionné dans le texte ?",
      options: [
        { id: "a", text: "Les salaires sont plus bas", correct: false },
        { id: "b", text: "Le travail est plus difficile", correct: false },
        { id: "c", text: "Il y a moins d'interactions sociales", correct: true },
        { id: "d", text: "Les horaires sont plus longs", correct: false }
      ]
    },
    {
      level: "B1",
      passage: "L'apprentissage d'une langue étrangère ne se limite pas à la mémorisation de vocabulaire et de règles grammaticales. Il implique également une compréhension approfondie de la culture et des nuances qui donnent vie à la langue. C'est pourquoi l'immersion reste la méthode la plus efficace.",
      question: "D'après le texte, pourquoi l'immersion est-elle efficace ?",
      options: [
        { id: "a", text: "Elle est moins chère", correct: false },
        { id: "b", text: "Elle permet de comprendre la culture et les nuances", correct: true },
        { id: "c", text: "Elle est plus rapide", correct: false },
        { id: "d", text: "Elle ne nécessite pas de grammaire", correct: false }
      ]
    },
    // B2 Level Questions (Upper Intermediate)
    {
      level: "B2",
      passage: "Le changement climatique représente l'un des défis majeurs de notre époque. Malgré les avertissements répétés des scientifiques, les mesures prises demeurent insuffisantes. La transition énergétique nécessite non seulement des investissements massifs, mais aussi un changement profond des mentalités et des habitudes de consommation.",
      question: "Selon le texte, que faut-il pour réussir la transition énergétique ?",
      options: [
        { id: "a", text: "Seulement de l'argent", correct: false },
        { id: "b", text: "Des investissements et un changement de mentalités", correct: true },
        { id: "c", text: "Attendre que le climat s'améliore", correct: false },
        { id: "d", text: "Ignorer les scientifiques", correct: false }
      ]
    },
    {
      level: "B2",
      passage: "La mondialisation a engendré des bouleversements économiques sans précédent. Si elle a favorisé l'essor du commerce international et l'émergence de nouvelles puissances économiques, elle a également accentué les inégalités et fragilisé certains secteurs industriels traditionnels. Cette réalité complexe exige une régulation internationale plus efficace.",
      question: "Quelle est la conséquence négative de la mondialisation mentionnée ?",
      options: [
        { id: "a", text: "La fin du commerce", correct: false },
        { id: "b", text: "L'augmentation des inégalités", correct: true },
        { id: "c", text: "La disparition des pays", correct: false },
        { id: "d", text: "L'arrêt de l'industrie", correct: false }
      ]
    },
    {
      level: "B2",
      passage: "L'intelligence artificielle suscite autant d'espoirs que d'inquiétudes. D'une part, elle promet des avancées considérables dans des domaines tels que la médecine et l'éducation. D'autre part, elle soulève des questions éthiques fondamentales concernant la protection des données personnelles et l'avenir du travail humain. Un débat démocratique s'impose.",
      question: "Pourquoi l'intelligence artificielle nécessite-t-elle un débat démocratique ?",
      options: [
        { id: "a", text: "Elle est trop chère", correct: false },
        { id: "b", text: "Elle pose des questions éthiques importantes", correct: true },
        { id: "c", text: "Elle ne fonctionne pas", correct: false },
        { id: "d", text: "Elle n'intéresse personne", correct: false }
      ]
    }
  ];

  const handleAnswer = (questionIndex, optionId) => {
    setAnswers({ ...answers, [questionIndex]: optionId });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResults = () => {
    let a1Score = 0;
    let a2Score = 0;
    let b1Score = 0;
    let b2Score = 0;

    questions.forEach((question, index) => {
      const selectedOption = answers[index];
      const correctOption = question.options.find(opt => opt.correct);
      
      if (selectedOption === correctOption.id) {
        switch (question.level) {
          case "A1":
            a1Score++;
            break;
          case "A2":
            a2Score++;
            break;
          case "B1":
            b1Score++;
            break;
          case "B2":
            b2Score++;
            break;
          default:
            break;
        }
      }
    });

    return { a1Score, a2Score, b1Score, b2Score };
  };

  const determineLevel = () => {
    const scores = calculateResults();
    const total = scores.a1Score + scores.a2Score + scores.b1Score + scores.b2Score;
    const percentage = (total / questions.length) * 100;

    // Determine level based on performance
    if (scores.b2Score >= 2 && scores.b1Score >= 2 && percentage >= 75) {
      return { level: "B2", description: "Upper Intermediate - You can understand complex texts and express yourself fluently" };
    } else if (scores.b1Score >= 2 && scores.a2Score >= 2 && percentage >= 60) {
      return { level: "B1", description: "Intermediate - You can understand main points and deal with most situations" };
    } else if (scores.a2Score >= 2 && scores.a1Score >= 2 && percentage >= 45) {
      return { level: "A2", description: "Elementary - You can communicate in simple tasks and understand frequent expressions" };
    } else {
      return { level: "A1", description: "Beginner - You can understand basic phrases and express simple needs" };
    }
  };

  const currentQ = questions[currentQuestion];

  if (!testStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
        <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Button
              variant="ghost"
              className="text-white hover:text-white/80 mb-4"
              onClick={() => navigate("/")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="test-title">
              French Level Test
            </h1>
            <p className="text-xl text-white/90">Test your reading comprehension</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="shadow-2xl">
            <CardContent className="p-8 sm:p-12">
              <h2 className="text-3xl font-bold text-[#04539F] mb-6">Welcome to the French Level Test!</h2>
              <div className="space-y-4 text-gray-700 mb-8">
                <p className="text-lg">This test will help you determine your French reading comprehension level from A1 (Beginner) to B2 (Upper Intermediate).</p>
                <div className="bg-[#04539F]/10 rounded-lg p-6 space-y-2">
                  <p className="font-semibold text-[#04539F]">Test Information:</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>12 questions total (3 per level)</li>
                    <li>Reading passages with multiple choice questions</li>
                    <li>Duration: approximately 15-20 minutes</li>
                    <li>You can navigate back to review your answers</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600 italic">Note: This test focuses on reading comprehension. For a complete evaluation, consider booking a session with Bernard for speaking and writing assessment.</p>
              </div>
              <Button
                size="lg"
                className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full py-6 text-lg"
                onClick={() => setTestStarted(true)}
                data-testid="start-test-btn"
              >
                Start Test
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showResults) {
    const result = determineLevel();
    const scores = calculateResults();
    const totalCorrect = scores.a1Score + scores.a2Score + scores.b1Score + scores.b2Score;

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
        <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-4xl font-bold text-white">Test Results</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="shadow-2xl mb-8">
            <CardContent className="p-8 sm:p-12 text-center">
              <div className="mb-8">
                <div className="inline-block bg-[#04539F] text-white rounded-full px-8 py-4 mb-4">
                  <span className="text-5xl font-bold">{result.level}</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{result.description}</h2>
                <p className="text-xl text-gray-600">You answered {totalCorrect} out of {questions.length} questions correctly</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#04539F]">{scores.a1Score}/3</div>
                  <div className="text-sm text-gray-600">A1 Questions</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#04539F]">{scores.a2Score}/3</div>
                  <div className="text-sm text-gray-600">A2 Questions</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#04539F]">{scores.b1Score}/3</div>
                  <div className="text-sm text-gray-600">B1 Questions</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#04539F]">{scores.b2Score}/3</div>
                  <div className="text-sm text-gray-600">B2 Questions</div>
                </div>
              </div>

              <div className="bg-[#04539F]/10 rounded-lg p-6 mb-8 text-left">
                <h3 className="text-xl font-bold text-[#04539F] mb-4">What's Next?</h3>
                <p className="text-gray-700 mb-4">
                  This test assessed your reading comprehension. For a complete evaluation including speaking, writing, and listening skills, I recommend booking a diagnostic session.
                </p>
                <Button
                  className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full"
                  onClick={() => navigate("/contact")}
                >
                  Contact Bernard
                </Button>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCurrentQuestion(0);
                    setAnswers({});
                    setShowResults(false);
                    setTestStarted(false);
                  }}
                  className="rounded-full"
                >
                  Retake Test
                </Button>
                <Button
                  className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full"
                  onClick={() => navigate("/")}
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
      <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-white">French Level Test</h1>
            <div className="text-white text-sm">
              Question {currentQuestion + 1} of {questions.length}
            </div>
          </div>
          <div className="mt-4 bg-white/20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-2xl">
          <CardContent className="p-8">
            <div className="mb-6">
              <span className="inline-block bg-[#04539F] text-white px-3 py-1 rounded-full text-sm font-semibold mb-4">
                Level {currentQ.level}
              </span>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <p className="text-gray-800 leading-relaxed italic">{currentQ.passage}</p>
              </div>
              <h3 className="text-xl font-bold text-[#04539F] mb-4">{currentQ.question}</h3>
            </div>

            <div className="space-y-3 mb-8">
              {currentQ.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleAnswer(currentQuestion, option.id)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    answers[currentQuestion] === option.id
                      ? 'border-[#04539F] bg-[#04539F]/10'
                      : 'border-gray-200 hover:border-[#04539F]/50'
                  }`}
                  data-testid={`option-${option.id}`}
                >
                  <div className="flex items-center">
                    <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                      answers[currentQuestion] === option.id
                        ? 'border-[#04539F] bg-[#04539F]'
                        : 'border-gray-300'
                    }`}>
                      {answers[currentQuestion] === option.id && (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className="font-medium text-gray-800">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="rounded-full"
                data-testid="previous-btn"
              >
                Previous
              </Button>
              <Button
                onClick={handleNext}
                disabled={!answers[currentQuestion]}
                className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full"
                data-testid="next-btn"
              >
                {currentQuestion === questions.length - 1 ? 'Finish Test' : 'Next'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FrenchTest;
